import React, { Component } from 'react';
import { connect } from 'react-redux';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import { DATATABLE_CONFIG, ZATCA_UNIT_SUBMENU_ID } from '../../../shared/Config';
import { HASACCESS } from '../../../shared/utility';
import Messages from '../../../shared/UI/Messages/Messages';
import * as actions from '../../../store/actions/index';
import { ZATCA_STATUS, zatcaUnitListFunction, invoiceTypeLabel, formatDate } from './zatcaShared';

/**
 * One ZATCA EGS unit per branch. Each unit gets its own certificate, invoice
 * counter and hash chain, so a unit is set up here and then onboarded before
 * its branch can issue e-invoices.
 */
class ZatcaUnits extends Component {
    state = {
        access: undefined,
        selectedItems: [],
        globalFilter: null
    }

    componentDidMount() {
        this.setState({ access: HASACCESS(ZATCA_UNIT_SUBMENU_ID) });
        this.props.getMasterTableList(this.listFunction());
    }

    listFunction = () => zatcaUnitListFunction(this.props.loggedUserDetail.CompanyId)

    delete = () => {
        const selected = this.state.selectedItems;
        if (selected.length === 0) {
            this.growl.show({ severity: 'error', summary: null, detail: 'Select the units to delete' });
            return;
        }
        if (selected.some(unit => unit.onboardingStatus !== 'NotOnboarded')) {
            this.growl.show({
                severity: 'error',
                summary: null,
                detail: 'Only units that have not started onboarding can be deleted. An onboarded unit has a certificate and an invoice chain.'
            });
            return;
        }
        const deleteIds = selected.map(unit => ({ DeleteID: unit.egsUnitId }));
        this.props.dynamicDeleteProcess('ZatcaEgsUnit', deleteIds, this.listFunction());
        this.setState({ selectedItems: [] });
    }

    branchTemplate = (rowData) => (
        <span>{rowData.branchName}{rowData.branchCode ? ' (' + rowData.branchCode + ')' : ''}</span>
    )

    invoiceTypeTemplate = (rowData) => invoiceTypeLabel(rowData.invoiceTypeFlags)

    statusTemplate = (rowData) => {
        const status = ZATCA_STATUS[rowData.onboardingStatus] || ZATCA_STATUS.NotOnboarded;
        return <div className="actionCenter">
            <Button type="button" label={status.label} className={status.className + ' p-button-rounded'} />
        </div>;
    }

    certificateTemplate = (rowData) => {
        if (!rowData.certificateValidUntil) return '—';
        const expired = new Date(rowData.certificateValidUntil) < new Date();
        return <span style={expired ? { color: '#c62828', fontWeight: 600 } : undefined}>
            {expired ? 'Expired ' : ''}{formatDate(rowData.certificateValidUntil)}
        </span>;
    }

    actionTemplate = (rowData) => {
        const access = this.state.access;
        return <div className="actionCenter">
            {(access !== undefined && access.editAccess !== false) &&
                <Link to={'/zatca-units/edit/' + rowData.egsUnitId}><Button type="button" icon="pi pi-pencil" className="p-button-info" tooltip="Edit" /></Link>
            }
            {(access !== undefined && access.viewAccess !== false) &&
                <Link to={'/zatca-units/view/' + rowData.egsUnitId}><Button type="button" icon="pi pi-eye" className="p-button-secondary" tooltip="View" /></Link>
            }
            {(access !== undefined && access.viewAccess !== false) &&
                <Link to={'/zatca-onboarding/' + rowData.egsUnitId}><Button type="button" icon="pi pi-key" className="p-button-warning" tooltip="Onboarding" /></Link>
            }
        </div>;
    }

    render() {
        const access = this.state.access;
        if (access !== undefined && access.viewAccess === false) {
            this.props.history.push('/dashboard');
        }

        const header = (
            <div style={{ textAlign: 'right' }}>
                <InputText type="search" onInput={(e) => this.setState({ globalFilter: e.target.value })} placeholder="Search..." />
            </div>
        );

        // The list shares redux state with other master pages, so only rows
        // that are ZATCA units are shown.
        const tableData = Array.isArray(this.props.tableLists)
            ? this.props.tableLists.filter(row => row && row.egsUnitId)
            : [];

        return (
            <div className="p-grid padding">
                {this.props.loading && <div className="page-loader"></div>}
                {this.props.error !== null && <Messages type="error" message={this.props.error} />}
                {this.props.success !== null && <Messages type="success" message={this.props.success} />}
                <Toast ref={(el) => this.growl = el} />
                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>
                        <h1>ZATCA Units</h1>
                        <p>One unit per branch. Set a unit up, then open Onboarding to connect it to ZATCA.</p>

                        <div className="p-grid">
                            <div className="p-col-12 p-lg-12">
                                <div className="mt1">
                                    {(access !== undefined && access.addAccess !== false) &&
                                        <Link to="/zatca-units/add/new"><Button style={{ float: 'right' }} label="Add Unit" className="ml1" icon="pi pi-plus" /></Link>
                                    }
                                    {(access !== undefined && access.deleteAccess !== false) &&
                                        <Button style={{ float: 'right' }} onClick={this.delete} label="Delete" icon="pi pi-trash" className="p-button-danger" />
                                    }
                                </div>
                            </div>

                            <div className="p-col-12" style={{ marginTop: '2em' }}>
                                <DataTable value={tableData}
                                    selection={this.state.selectedItems} onSelectionChange={e => this.setState({ selectedItems: e.value })}
                                    globalFilter={this.state.globalFilter} header={header} {...DATATABLE_CONFIG} >
                                    <Column selectionMode="multiple" style={{ width: '3em' }} />
                                    <Column field="branchName" header="Branch" body={this.branchTemplate} sortable={true} />
                                    <Column field="commonName" header="Unit Name" sortable={true} />
                                    <Column field="environment" header="Environment" sortable={true} />
                                    <Column field="invoiceTypeFlags" header="Invoice Types" body={this.invoiceTypeTemplate} />
                                    <Column field="onboardingStatus" header="Status" body={this.statusTemplate} sortable={true} />
                                    <Column field="certificateValidUntil" header="Certificate Valid Until" body={this.certificateTemplate} sortable={true} />
                                    <Column field="action" header="Action" body={this.actionTemplate} />
                                </DataTable>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => {
    return {
        loggedUserDetail: state.auth.userObject,
        loading: state.master.loading,
        error: state.master.error,
        success: state.master.success,
        tableLists: state.master.tableLists
    }
}

const mapDispatchToProps = dispatch => {
    return {
        dynamicDeleteProcess: (model, data, func) => dispatch(actions.dynamicDeleteProcess(model, data, func)),
        getMasterTableList: (table, companyId) => dispatch(actions.getMasterTableList(table, companyId))
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ZatcaUnits);
